/**
 * Created by rathompson on 10/12/23
 * File: MoveTenantModal.tsx
 * Project: cloud-mgmt-modules ©
 * ©
 */


import type { VFC } from 'react'
import { Bold, ModalHeader, ModalProps, useIsMounted } from '@panwds/react-ui'
import type { Tsg } from '@sparky/framework'
import StringTemplate from '../../../common/utils/StringTemplate'
import { TITLE_MOVE_TENANT } from '../../constants/LabelConstants'
import MoveTenantSelectContent from './MoveTenantSelectContent'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import MoveTenantPreviewContent from './MoveTenantPreviewContent'
import { safeSet } from '../../../common/utils/Utils'
import TenancyService from '../../services/TenancyService'
import { LoadTreeDataAsync } from '../../../common/actions/GlobalActions'
import GlobalContext from '../../../common/context/GlobalContext'
import ScreenLoader from '../../../common/components/screenLoader/ScreenLoader'
import { onExpandedKey, selectTreeNode } from '../../../common/reducers/actions/TreeReducer.actions'
import { Modal } from '../../../common/components/modal/Modal'

const PAGES = {
  SELECT: 'select',
  PREVIEW: 'preview'
}

const MoveTenantModal: VFC<ModalProps & {
  closeModal: () => void,
  tsg: Tsg
}> = ({ isOpen, closeModal, tsg }) => {

  const { dispatch } = useContext(GlobalContext)
  const isMounted = useIsMounted()

  const [page, setPage] = useState<string>(PAGES.SELECT)
  const [selected, setSelected] = useState<Tsg>()
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const title = StringTemplate(TITLE_MOVE_TENANT, [tsg?.display_name])

  const onClose = useCallback((moved_tsg: Tsg) => {
    closeModal()
  }, [closeModal])

  useEffect(() => {
    const modalMask = document.getElementsByClassName('tw-bg-blue-steel-700 tw-fixed tw-inset-0 tw-z-10 tw-transition-opacity tw-duration-200 tw-opacity-80')
    if (modalMask && modalMask.length) {
      (modalMask[0] as HTMLElement).style.zIndex = '1051'
    }
    else {
      const mask = document.getElementsByClassName('tw-bg-gray-900 tw-fixed tw-inset-0 tw-z-10 tw-transition-opacity tw-duration-200 tw-opacity-75')
      if (mask && mask.length) {
        (modalMask[0] as HTMLElement).style.zIndex = '1051'
      }
    }
  }, [isOpen])

  const onContinue = useCallback((selected: Tsg) => {
    safeSet(setSelected, selected, isMounted.current)
    safeSet(setPage, PAGES.PREVIEW, isMounted.current)
  }, [isMounted])

  const onBack = useCallback(() => {
    safeSet(setSelected, undefined, isMounted.current)
    safeSet(setPage, PAGES.SELECT, isMounted.current)
  }, [isMounted])

  const onSubmit = useCallback(async (tsg, parent) => {
    try {
      safeSet(setLoading, true, isMounted.current)
      const { data: moved_tsg } = await TenancyService.tenantServiceGroups.move(tsg.id, parent.id)
      await LoadTreeDataAsync(dispatch)
        .then(() => dispatch(onExpandedKey([moved_tsg.id, moved_tsg.parent_id])))
        .then(() => dispatch(selectTreeNode({ ...moved_tsg })))
      onClose(moved_tsg)
    }
    catch (error: any) {
      safeSet(setError, error.message, isMounted.current)
    }
    finally {
      safeSet(setLoading, false, isMounted.current)
    }
  }, [dispatch, onClose, isMounted])

  const content = useMemo(() => {
    switch (page) {
      case PAGES.PREVIEW:
        return <MoveTenantPreviewContent {...{ tsg, onBack, onSubmit }} selected={selected as Tsg} />
      case PAGES.SELECT:
      default:
        return <MoveTenantSelectContent {...{ tsg, onContinue, onClose }}/>
    }
  }, [onBack, onClose, onContinue, onSubmit, page, selected, tsg])

  return (
    <Modal
      {...{ isOpen }}
      onClose={closeModal}
      style={{ minWidth: '700px' }}
    >
      <ModalHeader {...{ title }}/>
      {loading && <ScreenLoader appearance='light'/> }
      {error && (
        <div className='cm-p-3'>
          <Bold appearance='semibold' addClassName='cm-text-error'>{error}</Bold>
        </div>
      )}
      {content}
    </Modal>
  )
}

export default MoveTenantModal
